import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgxGauge } from 'ngx-gauge';
import { TranslateService } from '@ngx-translate/core';
import { GoalTypeEnum } from 'src/app/enums/goal-type.enum';
import { TopicType } from 'src/app/enums/topic-type.enum';
import { GoalPermissionsHelper } from 'src/app/helpers/goal-permissions.helper';
import { GoalsHelper } from 'src/app/helpers/goals.helper';
import { Action } from 'src/app/models/action.model';
import { CommentConfig } from 'src/app/models/comment-config.model';
import { IResultModel, ResultModel } from 'src/app/models/general/result.model';
import { Goal } from 'src/app/models/goal.model';
import { GoalPeriod } from 'src/app/models/goalperiod.model';
import { Kpi } from 'src/app/models/kpi.model';
import { State } from 'src/app/models/state.model';
import { GoalService, StateService } from 'src/app/services';
import { ResponsiveGaugeDirective } from 'src/app/shared/directives';
import { DateHelper } from 'src/app/shared/helpers';
import { GaugeSettings } from 'src/app/shared/models';
import { ErrorMessageService } from 'src/app/shared/services';

@Component({
  selector: 'app-goal-view',
  templateUrl: './goal-view.component.html',
  styleUrl: './goal-view.component.scss'
})
export class GoalViewComponent implements AfterViewInit {

  @Input()
  public goal: Goal;

  @Input()
  public goalPeriod: GoalPeriod;

  @Output()
  public delete = new EventEmitter<number>(); 

  public state: State;

  @ViewChild(ResponsiveGaugeDirective) gaugeContainerDirective?: ResponsiveGaugeDirective;
  @ViewChild('itemGauge') itemGauge?: NgxGauge;

  public gaugeConfig: GaugeSettings = {
      breakpoints: [
          { maxWidth: 650, size: 62 },
          { maxWidth: 850, size: 70 },
          { maxWidth: 1150, size: 85 },
          { maxWidth: Infinity, size: 105 },
      ]
  };

  constructor(private router: Router, stateService: StateService, private goalService: GoalService, private msgService: ErrorMessageService, private translate: TranslateService) {
    this.state = stateService.state;
  }

  ngAfterViewInit(): void {
    if (this.itemGauge && this.gaugeContainerDirective) {
      this.gaugeContainerDirective.gauge = this.itemGauge;
      this.gaugeContainerDirective.debounceUpdate(); // az első méret frissítés
    }
  }

  public get panelResult(): number | null {
    if (this.goal == null || this.goal.result === undefined) return null;

    return this.goal.result;
  };

  public get panelWeight(): number {
    if (this.goal == null) return 0;
    return this.goal.weight ?? 0;
  };

  public get hasIndicators(): boolean {
    if (this.goal == null) return false;
    
    return [GoalTypeEnum.TextBasedGoal, GoalTypeEnum.NumericGoal].includes(this.goal.goalType);
  }

 public createCommentConfig(action: Action): CommentConfig {
        return {
            employeeId: this.state.me.value.employee?.id ?? 0,
            topicId: TopicType.GoalAction,
            topicRef: action.id,     // assessmentId
        } as CommentConfig;
  }

  public kpiResult(item: Kpi): number | null {
    if (item == null || item.achieved == null || item.planned == null) return null;

    const maxValue = this.goalPeriod?.reviewPeriod?.reviewPeriodGoalSettings?.numericGoalMaxResult;
    return Math.round(GoalsHelper.calculateKPIRatio(item.planned, item.achieved, item.calculationType, maxValue));
  }

  public onEdit() {
    
    if (this.goal.goalType === GoalTypeEnum.TextBasedGoal)
      this.router.navigate(['/text-based-goal/', this.goal.id]);
    else if (this.goal.goalType === GoalTypeEnum.NumericGoal)
      this.router.navigate(['/numeric-goal/', this.goal.id]);
  }

  public allowEdit(): boolean {
    return GoalPermissionsHelper.canGoalEdit(this.goalPeriod, this.goal, this.state);
  }

  public allowDelete(): boolean {
    return GoalPermissionsHelper.canGoalDelete(this.goalPeriod, this.state, this.goal);
  }

  public isKpiInExpectedRange(item: Kpi): boolean { 
    const maxResult = this.goalPeriod.reviewPeriod?.reviewPeriodGoalSettings?.numericGoalMaxResult;

    if (!item.minExpectation || !item.achieved) return true;

    const ratio = GoalsHelper.calculateKPIRatio(item.planned, item.achieved, item.calculationType, maxResult);
    return ratio >= (item.minExpectation ?? 0);
  }

  public onDelete() {
    if (confirm(this.translate.instant("GENERAL.DELETE_CONFIRM"))) {
      
      this.goalService.deleteGoal(this.goal.id).subscribe({
        next: () => {
          this.delete.emit(this.goal.id);
        },
        error: (err: IResultModel<ResultModel>) => {
          this.msgService.handleError<ResultModel>(err);
        }
      });
    }
  }

  // return the hungarian month name based on the month number from item.deadline
  public getMonthDeadline(item: Action): string {
    if (item == null || item.deadline == null) return '';

    return DateHelper.getMonthName(item.deadline);
  }

  
}
